import type { ActionFunction } from '~/types/route-args';
import invariant from 'tiny-invariant';
import { BOARD_TERMS } from '~/config/mood-board-terminology';
import type {
    RemoveMoodBoardPinsByPageMutation,
    RemoveMoodBoardPinsByPageMutationVariables,
} from 'generated/graphql';
import { RemoveMoodBoardPinsByPageDocument } from 'generated/documents/RemoveMoodBoardPinsByPageDocument';

export const REMOVE_INSPIRATION_ITEMS_BY_PAGE_API_ROUTE =
    '/actions/remove-items-from-mood-board-by-page';

const DEFAULT_FAILED_MESSAGE = `Failed to remove ${BOARD_TERMS.singular.toLowerCase()} items.`;

/**
 * Removes board pins by the target they reference — library item (page) IDs
 * for page pins, LibraryImage IDs for photo pins — unlike
 * REMOVE_BUILD_HUB_INSPIRATION_ITEMS_API_ROUTE, which removes by pin ID.
 * The save/remove UI only knows the saved target, not the pin.
 */
export const action: ActionFunction = async ({ request, context }) => {
    const data = await request.formData();

    invariant(data.get('boardId'), 'Board id required.');
    invariant(
        data.get('pageIds[]') || data.get('imageIds[]'),
        'Page ids or image ids required for remove.',
    );

    const variables: RemoveMoodBoardPinsByPageMutationVariables = {
        BoardID: data.get('boardId') as string,
        LibraryItemIDs: data.getAll('pageIds[]').map(String),
        LibraryImageIDs: data.getAll('imageIds[]').map(String),
    };

    try {
        const { data: result } = await context.client.mutate<
            RemoveMoodBoardPinsByPageMutation,
            RemoveMoodBoardPinsByPageMutationVariables
        >({
            mutation: RemoveMoodBoardPinsByPageDocument,
            variables,
        });

        const validResult = result?.moodBoardPinsRemoveByPage;
        if (!validResult) {
            return {
                buildHubUpsert: {
                    __typename: 'ServerError',
                    Message: DEFAULT_FAILED_MESSAGE,
                },
            };
        }

        return result;
    } catch (error) {
        return {
            buildHubUpsert: {
                __typename: 'ServerError',
                Message:
                    error instanceof Error
                        ? error.message
                        : DEFAULT_FAILED_MESSAGE,
            },
        };
    }
};
